import React, { useState } from 'react';
import { Mail, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';
import AppHeader from './AppHeader';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const productionHosts = [
        'constelabrasil.com.br',
        'www.constelabrasil.com.br',
        'app.constelabrasil.com.br',
      ];

      const env = productionHosts.includes(window.location.hostname) ? 'Produção' : 'Desenvolvimento';

      const data = {
        accessKey: "sf_8bcbe62al9d9fj457dcd82mh",
        subject: "Constela Brasil - Contato Via Site [" + env + "] - " + formData.subject,
        replyTo: "@",
        name: formData.name,
        email: formData.email,
        message: formData.message,
      };

      const url = "https://api.staticforms.xyz/submit";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };
  const whatsappNumber = '5513997701755';
  const whatsappMessage = encodeURIComponent('Olá! Gostaria de saber mais sobre a Constela Brasil.');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      <AppHeader />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-[#F3F6F1]">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3A5859] mb-6 leading-tight">
            Fale <span className="text-[#A56096]">Conosco</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-[#496566] mb-8 max-w-3xl mx-auto">
            Estamos aqui para ajudar. Entre em contato conosco!
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Email Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-16 h-16 bg-[#A56096]/10 rounded-full mb-6">
                <Mail className="w-8 h-8 text-[#A56096]" />
              </div>
              <h3 className="text-2xl font-bold text-[#3A5859] mb-4">
                Email
              </h3>
              <p className="text-[#496566] mb-6">
                Envie-nos uma mensagem detalhada e responderemos o mais breve possível.
              </p>
              <a 
                href="mailto:contato@constelabrasil.com.br"
                className="text-[#A56096] font-semibold hover:text-[#8B4A7D] transition-colors"
              >
                contato@constelabrasil.com.br
              </a>
            </div>

            {/* WhatsApp Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-16 h-16 bg-[#25D366]/10 rounded-full mb-6">
                <MessageCircle className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="text-2xl font-bold text-[#3A5859] mb-4">
                WhatsApp
              </h3>
              <p className="text-[#496566] mb-6">
                Fale conosco diretamente pelo WhatsApp para um atendimento mais rápido.
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#20BA5A] transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Enviar Mensagem
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-[#3A5859] mb-8 text-center">
              Envie sua Mensagem
            </h2>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-800">
                  Mensagem enviada com sucesso! Entraremos em contato em breve.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800">
                  Ocorreu um erro ao enviar a mensagem. Por favor, tente novamente ou entre em contato pelo WhatsApp.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <input type="text" name="honeypot" style={{display: "none"}} />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#3A5859] mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                    placeholder="Seu nome"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#3A5859] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-[#3A5859] mb-2">
                  Assunto *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all"
                  placeholder="Como podemos ajudar?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#3A5859] mb-2">
                  Mensagem *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A56096] focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Escreva sua mensagem aqui..."
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-[#A56096] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#8B4A7D] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#3A5859] mb-4">
            Procurando por Respostas Rápidas?
          </h2>
          <p className="text-[#496566] mb-8">
            Muitas dúvidas já foram respondidas em nossa página inicial
          </p>
          <Link
            to="/#faq"
            className="inline-flex items-center gap-2 bg-[#3A5859] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2A4849] transition-colors"
          >
            Ver Perguntas Frequentes
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3A5859] text-white py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm opacity-80">
            © 2025 Constela Brasil. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
